/**
 * Hook useInboxUnread
 *
 * Quantidade de conversas com mensagens não lidas na Central de atendimento do
 * WhatsApp (#252), para o indicador do menu. Recarrega (agrupando eventos próximos)
 * quando chega mensagem ou uma conversa muda (lida, assumida, devolvida).
 *
 * @author Equipe VendeAi
 * @since 2.6.0
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getUnreadSummary } from '@services/whatsappInboxService';
import { useSocketEvent } from './useWebSocket';

const REFRESH_DEBOUNCE_MS = 800;

export function useInboxUnread(enabled: boolean): number {
  const [count, setCount] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(() => {
    if (!enabled) return;
    getUnreadSummary()
      .then(summary => setCount(summary.conversations))
      .catch(() => {
        // Sem WhatsApp no plano ou erro de rede: sem indicador
      });
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const schedule = useCallback(() => {
    if (!enabled) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      refresh();
    }, REFRESH_DEBOUNCE_MS);
  }, [enabled, refresh]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  useSocketEvent('whatsapp:message', schedule);
  useSocketEvent('whatsapp:conversation', schedule);

  return enabled ? count : 0;
}

export default useInboxUnread;
